// DropdownMenu.tsx
import React, { useState, useRef, useEffect } from "react";
import { DotsHorizontalIcon } from "@heroicons/react/solid";

interface DropdownMenuProps {
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  onEdit,
  onDelete,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleEditClick = () => {
    if (!disabled) {
      onEdit();
      setIsOpen(false);
    }
  };

  const handleDeleteClick = () => {
    if (!disabled) {
      onDelete();
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        disabled={disabled}
        className={`p-2 rounded-full ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-200'} focus:outline-none`}
      >
        <DotsHorizontalIcon className="h-5 w-5 text-gray-500" />
      </button>
      {isOpen && !disabled && (
        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
          <div
            className="py-1"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="options-menu"
          >
            <button
              onClick={handleEditClick}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
              role="menuitem"
            >
              Edit Listing
            </button>
            <button
              onClick={handleDeleteClick}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
              role="menuitem"
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
